import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { TOPIC_TAGS } from '../lib/constants';
import { useI18n } from '../i18n';
import { SwipeCard } from './SwipeCard';
import type { SwipeDir } from '../lib/useSwipe';

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  allowed?: readonly string[] | null;
  rtl?: boolean;
  /** Back from the first topic card goes to the mood card (CheckInDeck owns that transition). */
  onBackToMood: () => void;
  /** After the last topic card (or "Nothing else"), hand back to CheckInDeck to move on —
   * carries the final topics array directly, since it's set in the same tick as this call
   * (the caller's own `value` prop is still last render's, one tick behind). */
  onAllDone: (topics: string[]) => void;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Design brief §4.4: one card per topic, swiped or tapped one at a time —
// right/✓ "On my mind", left/✕ "Not really" — rather than TopicTags' grid
// (still used by the flag-off path in CheckIn.tsx, untouched). Order is
// shuffled once per open tab (Fisher–Yates) except "other" ("Something
// else"), always last so it reads as a catch-all. The shuffle is never
// sent or stored — only the resulting picks are.
export function TopicCards({ value, onChange, allowed, rtl, onBackToMood, onAllDone }: Props) {
  const { t } = useI18n();
  const pool = useMemo(() => TOPIC_TAGS.filter((tg) => !allowed || allowed.includes(tg.value)), [allowed]);
  const [order] = useState<string[]>(() => {
    const rest = pool.filter((tg) => tg.value !== 'other').map((tg) => tg.value);
    const other = pool.some((tg) => tg.value === 'other') ? ['other'] : [];
    return [...shuffle(rest), ...other];
  });
  const [idx, setIdx] = useState(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const firstCard = useRef(true);
  const flingRef = useRef<((dir: SwipeDir) => void) | null>(null);

  useEffect(() => {
    if (firstCard.current) {
      firstCard.current = false;
      return;
    }
    heading.current?.focus();
  }, [idx]);

  if (order.length === 0) {
    // No topics offered at all (an empty question set) — nothing to show;
    // move straight on rather than stranding the student on a blank card.
    onAllDone(value);
    return null;
  }

  const code = order[idx];
  const tag = TOPIC_TAGS.find((tg) => tg.value === code)!;

  function commit(dir: SwipeDir) {
    const picked = dir === 'forward';
    const withoutThis = value.filter((v) => v !== code);
    const next = picked ? [...withoutThis, code] : withoutThis;
    onChange(next);
    if (idx + 1 < order.length) setIdx(idx + 1);
    else onAllDone(next);
  }

  function back() {
    if (idx === 0) onBackToMood();
    else setIdx(idx - 1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowRight' || e.key === 'y' || e.key === 'Y') {
      e.preventDefault();
      flingRef.current?.('forward');
    } else if (e.key === 'ArrowLeft' || e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      flingRef.current?.('back');
    }
  }

  return (
    <div className="sx-section" onKeyDown={onKeyDown}>
      <button type="button" className="sx-btn-ghost sx-topic-back" onClick={back}>
        {t['r4b.ci.back']}
      </button>
      <SwipeCard
        key={code}
        onCommit={commit}
        rtl={rtl}
        stampForward={t['r4b.ci.onMyMind']}
        stampBack={t['r4b.ci.notReally']}
        swipeRef={(api) => {
          flingRef.current = api.fling;
        }}
        className="sx-topic-card"
      >
        <h1 className="sx-title" id="topic-card-title" ref={heading} tabIndex={-1}>
          {t['r4b.ci.onYourMind']}
        </h1>
        <p className="visually-hidden" role="status">{t['r4b.ci.topicOf'](idx + 1, order.length)}</p>
        <div className="sx-topic-face">
          <span className="emoji" aria-hidden="true">{tag.emoji}</span>
          <p className="sx-topic-name">{t[`topic.${tag.value}` as 'topic.exams']}</p>
        </div>
        <p className="sx-hint sx-center" aria-hidden="true">{t['r4b.ci.topicOf'](idx + 1, order.length)}</p>
      </SwipeCard>
      <div className="sx-deck-nav">
        <button type="button" className="sx-btn-ghost" onClick={() => flingRef.current?.('back')}>
          <span aria-hidden="true">✕ </span>{t['r4b.ci.notReally']}
        </button>
        <button type="button" className="sx-btn" onClick={() => flingRef.current?.('forward')}>
          <span aria-hidden="true">✓ </span>{t['r4b.ci.onMyMind']}
        </button>
      </div>
      <p className="sx-center">
        <button type="button" className="link-btn" onClick={() => onAllDone(value)}>{t['r4b.ci.nothingElse']}</button>
      </p>
    </div>
  );
}
